import { Trans } from "@lingui/react/macro";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, CircularProgress, Collapse, Divider, Typography, useTheme } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { useState } from "react";
import Block from "@/components/Block";
import { useBookingContext } from "@/contexts/BookingContext";
import { useFormatPrice } from "@/hooks";
import BookingSearchDetails from "./BookingSearchDetails";
import CampgroundHeader from "./CampgroundHeader";
import PriceBreakdown from "./PriceBreakdown";

export default function BookingSummaryCard() {
  const { state, loadingStates } = useBookingContext();
  const isLoading = loadingStates.price.loading;
  const { formatPrice, formatPriceAlt } = useFormatPrice();
  const [priceExpanded, setPriceExpanded] = useState(false);
  const theme = useTheme();

  const headerImage = state.selectedCampsite?.images?.[0];
  const imageUrl = headerImage?.variants?.["4_3"]?.url;

  return (
    <Block sx={{ p: 0 }}>
      {/* Header Image */}
      {imageUrl && (
        <Box sx={{ width: "100%", height: 180, overflow: "hidden" }}>
          <img
            src={imageUrl}
            alt={headerImage?.title || ""}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      )}

      {/* Campground Info */}
      <Box sx={{ p: 3 }}>
        <CampgroundHeader variant="expanded" />
      </Box>

      <Divider />

      {/* Booking Details */}
      <Box sx={{ p: 3 }}>
        <BookingSearchDetails variant="expanded" />
      </Box>

      {/* Price Summary Section */}
      <Box sx={{ position: "relative" }}>
        {isLoading && (
          <CircularProgress
            size={16}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              zIndex: 1,
              transform: "translate(-50%, -50%)",
            }}
          />
        )}
        <Box
          sx={{
            bgcolor: lighten(theme.palette.primary.main, 0.85),
            opacity: isLoading ? 0.5 : 1,
          }}
        >
          <Box
            component="button"
            onClick={() => setPriceExpanded(!priceExpanded)}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              "&:hover": {
                bgcolor: lighten(theme.palette.primary.main, 0.8),
              },
            }}
            aria-expanded={priceExpanded}
            aria-controls="price-breakdown"
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 600, textAlign: "left" }}>
                {formatPrice(state.price?.total || 0)}
                {state.price?.currency_alt && (
                  <Typography
                    component="span"
                    variant="body1"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    ({formatPriceAlt(state.price.currency_alt.total)})
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: "left" }}>
                <Trans>Total price</Trans>
              </Typography>
            </Box>
            {priceExpanded ? (
              <ExpandMoreIcon sx={{ color: "text.primary" }} />
            ) : (
              <ChevronRightIcon sx={{ color: "text.primary" }} />
            )}
          </Box>
        </Box>
      </Box>

      {/* Price Breakdown - Collapsible */}
      <Collapse in={priceExpanded} id="price-breakdown">
        {state.price && (
          <Box sx={{ p: 3 }}>
            <PriceBreakdown price={state.price} />
          </Box>
        )}
      </Collapse>
    </Block>
  );
}
